import { tr } from 'mmfintech-commons'
import ClearIcon from '@images/icons/Clear-dark.svg?react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { CoreInput } from '../CoreInput/CoreInput'
import './promoCodeInput.scss'

export interface PromoCodeInputProps {
  /** Current promo code value */
  value?: string
  /** Callback when promo code changes */
  onChange?: (name: string, value: string) => void
  /** Callback when Add button is clicked */
  onApply?: (promoCode: string) => void
  /** Callback when Clear icon is clicked */
  onClear?: () => void
  /** Name attribute for the input */
  name?: string
  /** Placeholder text */
  placeholder?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether the component is in loading state */
  isLoading?: boolean
  /** Error message to display */
  error?: string
  /** Success message to display */
  success?: string
  /** Label for the input */
  label?: string
  /** Whether to show the Add button */
  showButton?: boolean
  /** Custom button text (default: "Add") */
  buttonText?: string
  /** Whether the promo code has been applied (shows clear icon instead of add button) */
  isApplied?: boolean
}

export const PromoCodeInput: React.FC<PromoCodeInputProps> = ({
  value = '',
  onChange,
  onApply,
  onClear,
  name = 'promoCode',
  placeholder = tr('FRONTEND.DEPOSIT.PROMO_PLACEHOLDER', 'Enter code'),
  disabled = false,
  isLoading = false,
  error,
  success,
  label = tr('FRONTEND.DEPOSIT.PROMO_PLACEHOLDER', 'Enter code'),
  showButton = true,
  buttonText = tr('FRONTEND.PROMO.BUTTON', 'Add'),
  isApplied = false
}) => {
  const [internalValue, setInternalValue] = useState(value)

  // Sync internal value with prop value changes (e.g., when parent clears it)
  useEffect(() => {
    setInternalValue(value)
  }, [value])

  const handleChange = (fieldName: string, newValue: string) => {
    const uppercaseValue = newValue.toUpperCase()
    setInternalValue(uppercaseValue)
    if (onChange) {
      onChange(fieldName, uppercaseValue)
    }
  }

  const handleApply = () => {
    if (onApply && internalValue.trim()) {
      onApply(internalValue.trim())
    }
  }

  const handleClear = () => {
    setInternalValue('')
    if (onChange) {
      onChange(name, '')
    }
    if (onClear) {
      onClear()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && internalValue.trim() && !isApplied) {
      handleApply()
    }
  }

  // Show clear icon when promo is applied, otherwise show add button (only if value is not empty)
  const RightIconContent = isApplied ? (
    isLoading ? (
      <span className='promo-code-clear-spinner' />
    ) : (
      <ClearIcon className='promo-code-clear-icon' onClick={handleClear} />
    )
  ) : showButton && internalValue.trim().length > 0 ? (
    <button
      className={`promo-code-add-button ${isLoading ? 'loading' : ''}`}
      onClick={handleApply}
      disabled={disabled || isLoading}
      type='button'
    >
      {isLoading ? <span className='spinner' /> : <span className='button-text'>{buttonText}</span>}
    </button>
  ) : undefined

  return (
    <div className='promo-code-input-wrapper'>
      <CoreInput
        name={name}
        label={label}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        readOnly={isApplied}
        RightIcon={RightIconContent}
        onKeyDown={handleKeyDown}
        error={error}
        className='promo-code-input'
        maxLength={64}
      />

      {success && (
        <div className='promo-code-success'>
          <span>{success}</span>
        </div>
      )}
    </div>
  )
}
